<template>
  <fts-modal
    :append-to-body="true"
    width="576px"
    :backdrop="!isCreating"
    :escape-event="!isCreating"
    header-class="workspace-create-modal__header"
    content-class="workspace-create-modal__content comfort-form-design"
    footer-class="workspace-create-modal__footer"
    class="workspace-create-modal"
    @close="close"
    v-if="isVisible"
  >
    <template #header>
      <template v-if="verification">
        <div class="workspace-create-modal-title">Verify your email to create a workspace</div>
        <div class="workspace-create-modal-description">
          We sent a 6-digit code to <span class="fts-font-medium">{{ verification.email }}</span>.
          Workspaces come with AI credits, so we check the address first.
        </div>
      </template>
      <template v-else-if="vendor">
        <div class="workspace-create-modal-title">Set up a workspace for a customer</div>
        <div class="workspace-create-modal-description">
          {{ formData.autocount
            ? "We invite your contact, then you connect their AutoCount."
            : "We invite your contact, then you set up their first workflow." }}
        </div>
      </template>
      <template v-else>
        <div class="workspace-create-modal-title">Create your workspace</div>
        <div class="workspace-create-modal-description">
          Create a new workspace to get started.
        </div>
      </template>
    </template>

    <template #body>
      <!-- Step-up (docs/identity-email-verification-proposal.md, D-6): an account that predates
           email verification proves its address once, in place, then creation continues by itself. -->
      <fts-verification-code
        v-if="verification"
        ref="code"
        :verification="verification"
        :busy="isVerifying || isCreating"
        :verified="verified"
        :error="verificationError"
        :show-button="false"
        compact
        @input="codeReady = $event.length === 6"
        @submit="confirmCode"
        @resend="resendCode"
      />
      <!-- The vendor's one form (docs/billing-vendor-lead-onboarding-proposal.md N2, in the SVN repo):
           the company and its contact, once. The server bills the workspace to the vendor and invites
           the contact as a Workspace Admin. -->
      <div v-else-if="vendor" class="workspace-create-modal-customer">
        <fts-login-form-element
          v-model="formData.name"
          type="workspace-name"
          display-name="Company name"
          placeholder="Company name, eg. ABC Trading Sdn Bhd"
          :validator="$v.formData && $v.formData.name"
          ref="nameInput"
        ></fts-login-form-element>
        <div class="workspace-create-modal-hint">
          Usually the company in their AutoCount account book. You can rename it later.
        </div>
        <fts-login-form-element
          v-model="formData.contactName"
          type="display-name"
          display-name="Contact name"
          placeholder="Contact name, eg. Ali bin Ahmad"
          :validator="$v.formData && $v.formData.contactName"
          ref="contactNameInput"
        ></fts-login-form-element>
        <fts-login-form-element
          v-model="formData.contactEmail"
          type="username"
          display-name="Contact e-mail"
          placeholder="Contact e-mail"
          :validator="$v.formData && $v.formData.contactEmail"
          ref="contactEmailInput"
        ></fts-login-form-element>
        <div class="workspace-create-modal-switch">
          <span>Connect AutoCount</span>
          <fts-toggle-switch v-model="formData.autocount" />
        </div>
        <div class="workspace-create-modal-note">
          Billed by {{ vendor.name }}. {{ contactLabel }} becomes an admin too. Inistate can make them the owner later.
        </div>
      </div>
      <fts-login-form-element
        v-else
        v-model="formData.name"
        type="workspace-name"
        display-name="Workspace name"
        :validator="$v.formData && $v.formData.name"
        ref="nameInput"
      ></fts-login-form-element>
    </template>

    <template #footer>
      <fts-btn
        lg
        text
        text-transform-init
        :disabled="isCreating || isVerifying"
        @click="close"
      >
        Cancel
      </fts-btn>

      <fts-btn
        lg
        color="primary"
        text-transform-init
        box-shadow-none
        :loading="isCreating || isVerifying"
        :disabled="verification && !verified && !codeReady"
        @click="verification ? submitCode() : submit()"
      >
        {{ verification ? 'Verify and create' : vendor ? 'Set up workspace' : 'Create' }}
      </fts-btn>
    </template>
  </fts-modal>
</template>

<script>
import { required, email } from "vuelidate/lib/validators";
import designValidator from "../commons/designValidator";
import { authorizationStore } from "../store/authorizationStore";
import LocalStorageService from "../store/LocalStorageService";
import webAppConfig from "../commons/webApp.config";
import FTSModal from "../elements/FTSModal";
import FTSBtn from "../elements/FTSBtn";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";
import FTSLoginFormElement from "../administration/FTSLoginFormElement";
import FTSVerificationCode from "../administration/FTSVerificationCode";

const emptyForm = () => ({ name: null, contactName: null, contactEmail: null, autocount: true });

export default {
  name: "WorkspaceCreateModal",

  components: {
    "fts-modal": FTSModal,
    "fts-btn": FTSBtn,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-login-form-element": FTSLoginFormElement,
    "fts-verification-code": FTSVerificationCode,
  },

  props: {
    workspaceType: {
      type: String,
      default: null,
    },
  },

  data: () => ({
    isVisible: false,
    isCreating: false,
    isVerifying: false,
    verification: null,
    verificationError: null,
    verified: false,
    codeReady: false,
    // The caller's vendor company ({ id, name }) when the switcher opened this for a customer.
    vendor: null,
    formData: emptyForm(),
  }),

  computed: {
    contactLabel() {
      const name = (this.formData.contactName || "").trim();
      return name || "Your contact";
    },
  },

  validations() {
    if (!this.isVisible) return { formData: {} };
    const name = { required, format: designValidator.workspaceNameFormat };
    if (!this.vendor) return { formData: { name } };
    return {
      formData: {
        name,
        contactName: { required },
        contactEmail: { required, email },
      },
    };
  },

  methods: {
    /** `options.vendor` opens the vendor's form for a new customer instead of the name-only box. */
    open(options) {
      this.vendor = (options && options.vendor) || null;
      this.isVisible = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },

    close() {
      this.$v.formData?.$reset();
      this.isVisible = false;
      this.isVerifying = false;
      this.verification = null;
      this.verificationError = null;
      this.verified = false;
      this.codeReady = false;
      this.vendor = null;
      this.formData = emptyForm();
    },

    async submit() {
      if (this.isCreating) return;

      try {
        this.$v.formData.$touch();

        if (this.$v.formData.$invalid) {
          this.$toasted.global.invalidFormValidate();
          return;
        }

        this.isCreating = true;

        let res;
        let customer = null;
        if (this.vendor) {
          const contactEmail = (this.formData.contactEmail || "").trim();
          res = await authorizationStore.dispatch("setupVendorCustomer", {
            companyName: this.formData.name,
            contactName: this.formData.contactName,
            contactEmail,
          });
          customer = {
            autocount: this.formData.autocount,
            contactEmail,
            invite: res?.data?.invite,
            inviteMessage: res?.data?.inviteMessage,
          };
        } else {
          const payload = { companyName: this.formData.name };
          if (this.workspaceType) payload.type = this.workspaceType;
          res = await authorizationStore.dispatch("setupWorkspace", payload);
        }
        const workspaceId = res?.data?.workspaceId;

        if (!workspaceId) {
          throw new Error("Workspace was created, but no workspace ID was returned. Please refresh and try again.");
        }

        $cookies.set("wsId", workspaceId);
        LocalStorageService.setWorkspaceId(webAppConfig.baseServer, workspaceId);

        this.close();
        if (customer) {
          this.announceInvite(customer);
          this.$emit("created", workspaceId, customer);
        } else {
          this.$emit("created", workspaceId);
        }
      } catch (error) {
        // Only the server knows whether the gate is on (Identity.EmailVerification.WorkspaceCreateEnabled),
        // so the step-up starts from its 403 alone. The profile's emailConfirmed is false for every
        // account that predates verification - gate on or off - so it must not pre-empt the request.
        if (error?.response?.status === 403 && error.response.data?.error === "email_not_verified") {
          this.isCreating = false;
          await this.startStepUp();
          return;
        }
        console.error(error);
        this.$toasted.global.error(error);
      } finally {
        this.isCreating = false;
      }
    },

    async startStepUp() {
      try {
        this.isVerifying = true;
        this.verificationError = null;
        const res = await authorizationStore.dispatch("verifyEmailStart");
        this.verification = res.verification;
        this.codeReady = false;
      } catch (error) {
        if (error?.response?.data?.error === "already_verified") {
          this.verified = true;
          await authorizationStore.dispatch("profile");
          return this.submit();
        }
        this.$toasted.error(this.messageFor(error));
      } finally {
        this.isVerifying = false;
      }
    },

    submitCode() {
      this.$refs.code && this.$refs.code.submit();
    },

    async confirmCode(code) {
      if (this.isVerifying || !this.verification) return;
      try {
        this.isVerifying = true;
        this.verificationError = null;
        await authorizationStore.dispatch("verifyEmailConfirm", { id: this.verification.id, code });
        this.verified = true;
        this.isVerifying = false;
        // The address is proven: finish what the user came for without a second click.
        await this.submit();
      } catch (error) {
        this.verificationError = this.messageFor(error);
        if (error?.response?.data?.error === "session_expired") {
          this.verification = null;
        }
      } finally {
        this.isVerifying = false;
      }
    },

    async resendCode() {
      if (!this.verification) return;
      try {
        this.isVerifying = true;
        this.verificationError = null;
        const res = await authorizationStore.dispatch("verifyEmailResend", { id: this.verification.id });
        this.verification = res.verification;
        this.$toasted.success(`We sent a new code to ${this.verification.email}.`);
      } catch (error) {
        this.verificationError = this.messageFor(error);
      } finally {
        this.isVerifying = false;
      }
    },

    /** The workspace exists whatever happened to the invitation; a failed one says where to send it again. */
    announceInvite({ invite, inviteMessage, contactEmail }) {
      if (invite === "invited") {
        this.$toasted.success(`Invitation sent to ${contactEmail}. They join as an admin.`);
      } else if (invite === "joined") {
        this.$toasted.success(`${contactEmail} already had an account and is now an admin here.`);
      } else if (invite === "failed") {
        this.$toasted.error(inviteMessage || `The invitation to ${contactEmail} didn't go. Invite them from Settings › People.`);
      }
    },

    messageFor(error) {
      const data = error?.response?.data;
      if (data && (data.error_description || data.message)) return data.error_description || data.message;
      if (typeof error === "string") return error;
      return "Something went wrong. Please try again.";
    },
  },

};
</script>

<style lang="scss">
.workspace-create-modal {
  &__header {
    padding: 24px 24px 16px;
    text-align: center;
  }

  &__content {
    padding: 16px 24px;

    .fts-verification-code {
      align-items: center;
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    padding: 16px 24px 24px;
  }

  &-title {
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-heading-text-20);
    line-height: var(--inistate-heading-text-20--line-height);
    color: var(--inistate-text-primary);
  }

  &-description {
    margin-top: 4px;
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-paragraph-text-16--line-height);
    color: var(--inistate-text-secondary);
  }

  &-hint {
    margin: -8px 0 16px;
    font-size: var(--inistate-paragraph-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    color: var(--inistate-text-secondary);
  }

  &-switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 4px 0 16px;
    font-size: var(--inistate-paragraph-text-16);
    color: var(--inistate-text-primary);
  }

  &-note {
    padding: 12px;
    border-radius: 6px;
    background: var(--inistate-background-secondary, rgba(0, 0, 0, 0.04));
    font-size: var(--inistate-paragraph-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    color: var(--inistate-text-secondary);
  }
}
</style>
